import { Box, Button, Flex, Text, Textarea, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { formatAssistTokenUsage } from 'pages/Cockpit/QuickCashflowModel/applyAssistResponse';
import AssistChatMarkdown from './AssistChatMarkdown';
import {
  buildAssistChatHistory,
  CLEAR_CHAT_TOOLTIP,
  clearedChatMessages,
  shouldConfirmChatClear,
} from './cockpitAssistChatUtils';

function sanitizeAssistError(message) {
  if (!message) return message;
  const lines = String(message).split('\n');
  const clean = [];
  for (const line of lines) {
    const t = line.trim();
    if (/^(github\.com\/|\/Users\/|\/home\/)/.test(t)) {
      break;
    }
    clean.push(line);
  }
  return clean.join('\n').trim();
}

function CockpitAssistChat({
  onSend,
  placeholder,
  annotation,
  acceptFiles,
  disabled,
  maxHeight = '360px',
  initialMessages = [],
  droppedFiles = [],
  onDroppedFilesConsumed,
  quickActions = [],
  showBack = false,
  backMessage = '__nav:back',
  appendMessage = null,
}) {
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const abortRef = useRef(null);
  const [chatMessages, setChatMessages] = useState(initialMessages);
  const [chatInput, setChatInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [awaitingMappingConfirmation, setAwaitingMappingConfirmation] =
    useState(false);
  const [pendingCohortColumnMapping, setPendingCohortColumnMapping] =
    useState(null);

  useEffect(() => {
    if (!droppedFiles?.length) return;
    setPendingFiles((pf) => [...pf, ...droppedFiles]);
    onDroppedFilesConsumed?.();
  }, [droppedFiles, onDroppedFilesConsumed]);

  const scrollMessagesToBottom = () => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 64;
  };

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    requestAnimationFrame(scrollMessagesToBottom);
  }, [chatMessages, loading]);

  useEffect(() => {
    if (!appendMessage?.id) return;
    setChatMessages((messages) => {
      if (messages.some((msg) => msg.id === appendMessage.id)) {
        return messages;
      }
      return [
        ...messages,
        {
          id: appendMessage.id,
          role: 'ai',
          text: appendMessage.text,
          usage: appendMessage.usage,
        },
      ];
    });
    stickToBottomRef.current = true;
  }, [appendMessage?.id, appendMessage?.text, appendMessage?.usage]);

  const addFiles = (fileList) => {
    const next = [...pendingFiles];
    Array.from(fileList || []).forEach((f) => next.push(f));
    setPendingFiles(next);
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text && pendingFiles.length === 0) return;
    if (disabled) return;

    await sendWithPayload(text);
  };

  const sendWithPayload = async (text) => {
    const files = [...pendingFiles];
    const userLabel = text || '(file attached)';
    const history = buildAssistChatHistory(chatMessages);

    setChatMessages((m) => [
      ...m,
      { role: 'user', text: userLabel, files: files.map((f) => f.name) },
    ]);
    stickToBottomRef.current = true;
    setChatInput('');
    setPendingFiles([]);
    setLoading(true);

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await onSend({
        message: text,
        files,
        history,
        cohortColumnMapping: pendingCohortColumnMapping,
        signal: controller.signal,
      });
      const aiText = result?.text || 'Done.';
      if (result?.awaitingMappingConfirmation) {
        setAwaitingMappingConfirmation(true);
        setPendingCohortColumnMapping(result.cohortColumnMapping ?? null);
      } else {
        setAwaitingMappingConfirmation(false);
        setPendingCohortColumnMapping(null);
      }
      setChatMessages((m) => [
        ...m,
        {
          role: 'ai',
          text: aiText,
          usage: formatAssistTokenUsage(result?.usage),
        },
      ]);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        setChatMessages((m) => [
          ...m,
          { role: 'ai', text: 'Request cancelled.' },
        ]);
        return;
      }
      const raw = err?.response?.data?.error || err.message;
      const msg = sanitizeAssistError(raw);
      setChatMessages((m) => [
        ...m,
        { role: 'ai', text: `Sorry, I could not complete that: ${msg}` },
      ]);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const cancelRequest = () => {
    abortRef.current?.abort();
  };

  const clearChat = () => {
    if (loading || disabled) return;
    if (
      shouldConfirmChatClear(chatMessages, initialMessages) &&
      !window.confirm(
        'Clear this conversation? Menu progress and model data are kept.'
      )
    ) {
      return;
    }
    abortRef.current?.abort();
    setLoading(false);
    abortRef.current = null;
    setChatMessages(clearedChatMessages(initialMessages));
    setChatInput('');
    setPendingFiles([]);
    setAwaitingMappingConfirmation(false);
    setPendingCohortColumnMapping(null);
    stickToBottomRef.current = true;
  };

  return (
    <Box className="cockpit-assist-chat">
      {annotation ? (
        <Text className="annotation" mb={3}>
          {annotation}
        </Text>
      ) : null}
      <Flex justify="flex-end" mb={2}>
        <Tooltip label={CLEAR_CHAT_TOOLTIP} hasArrow placement="top">
          <Button
            size="sm"
            variant="ghost"
            colorScheme="purple"
            onClick={clearChat}
            isDisabled={disabled || loading}
          >
            Clear chat
          </Button>
        </Tooltip>
      </Flex>
      <Flex
        ref={messagesContainerRef}
        className="chat-thread"
        direction="column"
        gap={3}
        maxH={maxHeight}
        overflowY="auto"
        mb={3}
        onScroll={handleMessagesScroll}
      >
        {chatMessages.map((msg, i) => (
          <Box
            key={`${msg.role}-${i}`}
            className={`chat-bubble ${
              msg.role === 'user' ? 'ai-bubble-user' : 'ai-bubble-ai chat-bubble-markdown'
            }`}
          >
            {msg.role === 'user' ? msg.text : <AssistChatMarkdown text={msg.text} />}
            {msg.usage ? (
              <Text className="chat-usage" mt={2}>
                {msg.usage}
              </Text>
            ) : null}
            {msg.files?.length ? (
              <Flex mt={2} gap={1} flexWrap="wrap">
                {msg.files.map((f) => (
                  <span key={f} className="file-chip">
                    {f}
                  </span>
                ))}
              </Flex>
            ) : null}
          </Box>
        ))}
        {loading ? (
          <Flex align="center" gap={2}>
            <Box className="chat-bubble ai-bubble-ai">Analyzing…</Box>
            <Button size="sm" variant="outline" onClick={cancelRequest}>
              Cancel
            </Button>
          </Flex>
        ) : null}
      </Flex>
      {awaitingMappingConfirmation ? (
        <Text className="annotation" color="purple.600" mb={2}>
          Reply yes to confirm the proposed mapping, or describe corrections in chat.
        </Text>
      ) : null}
      {acceptFiles && pendingFiles.length > 0 ? (
        <Flex gap={2} mb={2} flexWrap="wrap">
          {pendingFiles.map((f, i) => (
            <span key={`${f.name}-${i}`} className="file-chip">
              {f.name}
              <button
                type="button"
                onClick={() =>
                  setPendingFiles((pf) => pf.filter((_, j) => j !== i))
                }
              >
                ×
              </button>
            </span>
          ))}
        </Flex>
      ) : null}
      {showBack ? (
        <Flex mb={2}>
          <Button
            size="sm"
            variant="ghost"
            colorScheme="purple"
            isDisabled={disabled || loading}
            onClick={() => sendWithPayload(backMessage)}
          >
            Back
          </Button>
        </Flex>
      ) : null}
      {quickActions?.length ? (
        <Flex gap={2} mb={2} flexWrap="wrap">
          {quickActions.map((action) => (
            <Button
              key={action.label}
              size="sm"
              variant="outline"
              colorScheme="purple"
              isDisabled={disabled || loading}
              onClick={() => sendWithPayload(action.message)}
            >
              {action.label}
            </Button>
          ))}
        </Flex>
      ) : null}
      <Flex gap={2} align="flex-end">
        {acceptFiles ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              isDisabled={disabled || loading}
            >
              Attach
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept={acceptFiles}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </>
        ) : null}
        <Textarea
          flex={1}
          rows={1}
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder={placeholder}
          isDisabled={disabled || loading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendChat();
            }
          }}
        />
        <Button
          colorScheme="purple"
          onClick={sendChat}
          isLoading={loading}
          isDisabled={disabled}
        >
          Send
        </Button>
      </Flex>
    </Box>
  );
}

CockpitAssistChat.propTypes = {
  onSend: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  annotation: PropTypes.string,
  acceptFiles: PropTypes.string,
  disabled: PropTypes.bool,
  maxHeight: PropTypes.string,
  initialMessages: PropTypes.array,
  droppedFiles: PropTypes.array,
  onDroppedFilesConsumed: PropTypes.func,
  quickActions: PropTypes.array,
  showBack: PropTypes.bool,
  backMessage: PropTypes.string,
  appendMessage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    usage: PropTypes.string,
  }),
};

export default CockpitAssistChat;
